import { useState } from "react";
import { Send, Trash2, Pencil, Check, X } from "lucide-react";
import api from "../lib/api";
import Avatar from "./Avatar";
import { relativeTime } from "../lib/format";

/**
 * Generic comment thread used by opportunities, deals, and meetings.
 *
 *   apiPrefix    e.g. "/deals/abc-123"  (full prefix; we append /comments[/id])
 *   comments     array of comment objects (id, text, author, author_id, created_at, edited_at)
 *   currentUser  used for author-or-admin permission checks
 *   onUpdated    receives the updated parent object returned by the server
 *   canPost      whether the current user is allowed to post
 *   placeholder  textarea placeholder
 *   emptyHint    text shown when there are no comments
 *   forbiddenHint text shown to non-team viewers (when canPost is false)
 */
function CommentItem({ c, apiPrefix, currentUser, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.text);
  const canModify = c.author_id === currentUser.id || currentUser.role === "admin";

  const save = async () => {
    if (!draft.trim()) return;
    const { data } = await api.patch(`${apiPrefix}/comments/${c.id}`, { text: draft });
    setEditing(false);
    onUpdated(data);
  };
  const onDelete = async () => {
    if (!window.confirm("Delete this comment?")) return;
    const { data } = await api.delete(`${apiPrefix}/comments/${c.id}`);
    onUpdated(data);
  };

  return (
    <li className="flex items-start gap-3 group" data-testid={`comment-${c.id}`}>
      <Avatar name={c.author?.name || "?"} photoUrl={c.author?.photo_url} color={c.author?.avatar_color || "#C9A84C"} size={32} />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-[#9A9590] flex items-center gap-2">
          <span className="text-[#F0EDE8]">{c.author?.name || "Member"}</span>
          <span>·</span>
          <span>{relativeTime(c.created_at)}</span>
          {c.edited_at && <span className="italic text-[#9A9590]/70">· edited</span>}
        </div>
        {editing ? (
          <div className="mt-1.5 space-y-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              autoFocus
              className="ts-input resize-none py-2"
              data-testid={`comment-edit-input-${c.id}`}
            />
            <div className="flex gap-2">
              <button onClick={save} className="btn-gold rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1" data-testid={`comment-save-${c.id}`}>
                <Check size={12}/> Save
              </button>
              <button onClick={() => { setEditing(false); setDraft(c.text); }} className="text-xs text-[#9A9590] hover:text-[#F0EDE8] inline-flex items-center gap-1">
                <X size={12}/> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2 mt-0.5">
            <div className="text-sm text-[#F0EDE8] leading-relaxed flex-1 whitespace-pre-line">{c.text}</div>
            {canModify && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 shrink-0">
                <button onClick={() => setEditing(true)} className="text-[#9A9590] hover:text-[#C9A84C]" data-testid={`comment-edit-${c.id}`}>
                  <Pencil size={13} />
                </button>
                <button onClick={onDelete} className="text-[#9A9590] hover:text-red-400" data-testid={`comment-delete-${c.id}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export default function CommentThread({
  apiPrefix,
  comments = [],
  currentUser,
  onUpdated,
  canPost = true,
  placeholder = "Add a note for the team…",
  emptyHint = "No comments yet.",
  forbiddenHint = "Only the team can post here.",
}) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`${apiPrefix}/comments`, { text });
      onUpdated(data);
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="comment-thread">
      <ul className="space-y-4 mb-4 max-h-[360px] overflow-y-auto pr-2" data-testid="comments-list">
        {comments.map((c) => (
          <CommentItem
            key={c.id}
            c={c}
            apiPrefix={apiPrefix}
            currentUser={currentUser}
            onUpdated={onUpdated}
          />
        ))}
        {comments.length === 0 && <li className="text-sm text-[#9A9590]">{emptyHint}</li>}
      </ul>
      {canPost ? (
        <form onSubmit={submit} className="flex items-end gap-2 border-t border-[#C9A84C]/12 pt-3" data-testid="comment-form">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            rows={2}
            className="ts-input flex-1 resize-none"
            data-testid="comment-input"
          />
          <button type="submit" disabled={sending} className="btn-gold rounded-md px-4 py-2.5 inline-flex items-center gap-2 text-sm disabled:opacity-60" data-testid="comment-submit">
            <Send size={14} /> {sending ? "Sending…" : "Send"}
          </button>
        </form>
      ) : (
        <div className="border-t border-[#C9A84C]/12 pt-3 text-xs text-[#9A9590] italic">{forbiddenHint}</div>
      )}
    </div>
  );
}
